import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import PlantillaModal from '../util/PlantillaModal';
import instanciaAxios from '../../miscelaneo/instanciaAxios';
import { roundTo2 } from '../../miscelaneo/monetario';

const TASAS_ITBIS = { 1: 18, 2: 16, 3: 0, 4: 0 };

const OPCIONES_IMPUESTO = [
  { valor: 1, label: 'ITBIS 18%', descripcion: 'Tasa normal' },
  { valor: 2, label: 'ITBIS 16%', descripcion: 'Tasa reducida' },
  { valor: 3, label: 'ITBIS 0%', descripcion: 'Gravado sin impuesto' },
  { valor: 4, label: 'Exento', descripcion: 'Sin impuesto aplicable' },
];

// IDs de unidades más comunes para mostrar primero
const UNIDADES_COMUNES = [43, 34, 23, 21, 6, 13]; // Unidad, Pieza, Libra, KG, Caja, Docena

const ESTADO_INICIAL = {
  descripcion: '',
  codigo_barra: '',
  referencia: '',
  precio: '',
  indicador_facturacion: 1,
  id_unidad_medida: 43,
  id_tipo_producto: 1,
  venta_negativo: true,
};

export default function ModalCrearProductoRapido({
  mostrar,
  onClose,
  onProductoCreado,
  descripcionInicial = ''
}) {
  const [datosProducto, setDatosProducto] = useState({ ...ESTADO_INICIAL });
  const [unidadesMedida, setUnidadesMedida] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [mostrarOpciones, setMostrarOpciones] = useState(false);
  const [unidadesCargadas, setUnidadesCargadas] = useState(false);
  const [precioIncluyeItbis, setPrecioIncluyeItbis] = useState(false);

  const inputDescripcionRef = useRef(null);
  const inputPrecioRef = useRef(null);

  // Cargar unidades de medida la primera vez que se abre
  useEffect(() => {
    if (mostrar && !unidadesCargadas) {
      cargarUnidadesMedida();
    }
  }, [mostrar]);

  // Pre-fill descripción y autofocus cuando se abre
  useEffect(() => {
    if (mostrar) {
      if (descripcionInicial) {
        setDatosProducto(prev => ({ ...prev, descripcion: descripcionInicial }));
      }
      setTimeout(() => inputDescripcionRef.current?.focus(), 200);
    }
  }, [mostrar, descripcionInicial]);

  const cargarUnidadesMedida = async () => {
    try {
      const response = await instanciaAxios.get('/producto/unidad_medida');
      // Ordenar: comunes primero, luego el resto alfabéticamente
      const todas = response.data || [];
      const comunes = [];
      const resto = [];
      for (const u of todas) {
        if (UNIDADES_COMUNES.includes(u.id_unidad_medida)) {
          comunes.push(u);
        } else {
          resto.push(u);
        }
      }
      // Mantener el orden de UNIDADES_COMUNES
      comunes.sort((a, b) => 
        UNIDADES_COMUNES.indexOf(a.id_unidad_medida) - UNIDADES_COMUNES.indexOf(b.id_unidad_medida)
      );
      resto.sort((a, b) => a.descripcion.localeCompare(b.descripcion));
      setUnidadesMedida([...comunes, ...resto]);
      setUnidadesCargadas(true);
    } catch (error) {
      console.error('Error al cargar unidades de medida:', error);
    }
  };

  const limpiarFormulario = () => {
    setDatosProducto({ ...ESTADO_INICIAL });
    setMostrarOpciones(false);
    setPrecioIncluyeItbis(false);
  };

  const handleClose = () => {
    limpiarFormulario();
    onClose();
  };

  const crearProducto = async () => {
    // Validaciones
    const errores = [
      { cond: !datosProducto.descripcion.trim(), msg: 'La descripción es obligatoria' },
      { cond: !datosProducto.precio || parseFloat(datosProducto.precio) <= 0, msg: 'El precio debe ser mayor a 0' },
      { cond: ![1, 2, 3, 4].includes(datosProducto.indicador_facturacion), msg: 'Seleccione un tipo de impuesto válido' },
    ];

    for (let error of errores) {
      if (error.cond) {
        toast.error(error.msg);
        return;
      }
    }

    try {
      setGuardando(true);

      // Calcular precio base (siempre enviar precio sin impuestos al backend)
      const precioIngresado = parseFloat(datosProducto.precio);
      const tasaItbis = TASAS_ITBIS[datosProducto.indicador_facturacion] || 0;
      const precioBaseParaBackend = roundTo2(
        precioIncluyeItbis && tasaItbis > 0
          ? precioIngresado / (1 + tasaItbis / 100)
          : precioIngresado
      );

      const payload = {
        descripcion: datosProducto.descripcion.trim(),
        codigo_barra: datosProducto.codigo_barra?.trim() || null,
        referencia: datosProducto.referencia?.trim() || null,
        precio: precioBaseParaBackend,
        indicador_facturacion: datosProducto.indicador_facturacion,
        id_unidad_medida: datosProducto.id_unidad_medida,
        id_tipo_producto: datosProducto.id_tipo_producto,
        venta_negativo: datosProducto.venta_negativo,
        costo_inicial: 0,
        origen: 1,
      };

      const response = await instanciaAxios.post('/producto', payload);

      // Construir objeto producto en el formato que handleProductoSeleccionado espera
      const unidadSeleccionada = unidadesMedida.find(
        u => u.id_unidad_medida === datosProducto.id_unidad_medida
      );

      const productoParaTicket = {
        id_producto: response.data.id_producto,
        descripcion: datosProducto.descripcion.trim(),
        precio: precioBaseParaBackend, // Usar precio base (sin impuestos)
        indicador_facturacion: datosProducto.indicador_facturacion,
        itbis_tasa: TASAS_ITBIS[datosProducto.indicador_facturacion] || 0,
        id_tipo_producto: datosProducto.id_tipo_producto,
        descripcion_unidad_medida: unidadSeleccionada?.descripcion || 'Unidad',
      };

      toast.success(`Producto "${datosProducto.descripcion.trim()}" creado`);
      limpiarFormulario();
      onProductoCreado(productoParaTicket);
    } catch (error) {
      console.error('Error al crear producto:', error);
      const mensaje = error.response?.data?.mensaje || error.response?.data?.message || 'Error al crear producto';
      toast.error(mensaje);
    } finally {
      setGuardando(false);
    }
  };

  // Cálculos en tiempo real (siempre redondeados a 2 decimales)
  const precioIngresado = parseFloat(datosProducto.precio) || 0;
  const tasaItbis = TASAS_ITBIS[datosProducto.indicador_facturacion] || 0;
  
  let precioBase, montoItbis, precioTotal;
  
  if (precioIncluyeItbis) {
    // Usuario ingresó precio CON impuestos → calcular base
    precioTotal = roundTo2(precioIngresado);
    precioBase = roundTo2(tasaItbis > 0 ? precioTotal / (1 + tasaItbis / 100) : precioTotal);
    montoItbis = roundTo2(precioTotal - precioBase);
  } else {
    // Usuario ingresó precio SIN impuestos (modo tradicional)
    precioBase = roundTo2(precioIngresado);
    montoItbis = roundTo2(precioBase * (tasaItbis / 100));
    precioTotal = roundTo2(precioBase + montoItbis);
  }

  return (
    <PlantillaModal
      mostrar={mostrar}
      onClose={handleClose}
      titulo="Crear Nuevo Producto"
    >
      <div className="space-y-4">
        {/* 1. DESCRIPCIÓN */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Descripción <span className="text-red-500">*</span>
          </label>
          <input
            ref={inputDescripcionRef}
            type="text"
            value={datosProducto.descripcion}
            onChange={(e) => setDatosProducto({ ...datosProducto, descripcion: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') inputPrecioRef.current?.focus();
            }}
            className="w-full p-2 rounded-xl border-2 outline-none transition-all border-red-200 focus:border-red-500 focus:ring-2 focus:ring-red-100"
            placeholder="Nombre del producto"
            maxLength={200}
          />
        </div>

        {/* 2. CÓDIGO DE BARRAS */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Código de barras
          </label>
          <input
            type="text"
            value={datosProducto.codigo_barra}
            onChange={(e) => setDatosProducto({ ...datosProducto, codigo_barra: e.target.value })}
            className="w-full p-2 rounded-xl border-2 outline-none transition-all border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            placeholder="Escanear o escribir código de barras"
          />
        </div>

        {/* 3. PRECIO */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-gray-700">
              Precio de venta <span className="text-red-500">*</span>
            </label>
            <button
              type="button"
              onClick={() => setPrecioIncluyeItbis(!precioIncluyeItbis)}
              className="text-xs text-color-primario hover:text-color-secundario underline transition-colors"
            >
              {precioIncluyeItbis ? '✓ Incluye ITBIS' : 'Sin ITBIS'}
            </button>
          </div>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
            <input
              ref={inputPrecioRef}
              type="number"
              step="0.01"
              min="0.01"
              value={datosProducto.precio}
              onChange={(e) => setDatosProducto({ ...datosProducto, precio: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') crearProducto();
              }}
              className="w-full p-2 pl-8 text-lg font-semibold rounded-xl border-2 outline-none transition-all border-red-200 focus:border-red-500 focus:ring-2 focus:ring-red-100"
              placeholder={precioIncluyeItbis ? "Precio final (con ITBIS)" : "Precio base (sin ITBIS)"}
            />
          </div>
        </div>

        {/* 4. IMPUESTO - Radio buttons visuales */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Impuesto (ITBIS) <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 gap-2">
            {OPCIONES_IMPUESTO.map((opcion) => (
              <button
                key={opcion.valor}
                type="button"
                onClick={() => setDatosProducto({ ...datosProducto, indicador_facturacion: opcion.valor })}
                className={`p-2.5 rounded-xl border-2 text-left transition-all ${
                  datosProducto.indicador_facturacion === opcion.valor
                    ? 'border-color-secundario bg-blue-50 ring-2 ring-blue-100'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                <span className={`block text-sm font-semibold ${
                  datosProducto.indicador_facturacion === opcion.valor
                    ? 'text-color-secundario'
                    : 'text-gray-700'
                }`}>
                  {opcion.label}
                </span>
                <span className="block text-xs text-gray-500">{opcion.descripcion}</span>
              </button>
            ))}
          </div>
        </div>

        {/* PREVIEW DE PRECIO */}
        {precioIngresado > 0 && (
          <div className="bg-gray-50 p-3 rounded-xl border border-gray-200">
            <div className="space-y-1 text-sm">
              {precioIncluyeItbis ? (
                // Modo: precio con ITBIS → mostrar desglose
                <>
                  <div className="flex justify-between text-gray-600">
                    <span>Precio ingresado (con ITBIS):</span>
                    <span>${precioIngresado.toFixed(2)}</span>
                  </div>
                  <div className="border-t border-gray-200 pt-1 mt-1">
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>Precio base:</span>
                      <span>${precioBase.toFixed(2)}</span>
                    </div>
                    {tasaItbis > 0 && (
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>ITBIS ({tasaItbis}%):</span>
                        <span>${montoItbis.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                // Modo: precio sin ITBIS → mostrar total
                <>
                  <div className="flex justify-between text-gray-600">
                    <span>Precio base:</span>
                    <span>${precioBase.toFixed(2)}</span>
                  </div>
                  {tasaItbis > 0 && (
                    <div className="flex justify-between text-gray-600">
                      <span>ITBIS ({tasaItbis}%):</span>
                      <span>+ ${montoItbis.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="border-t border-gray-300 pt-1 mt-1">
                    <div className="flex justify-between font-bold text-gray-800">
                      <span>Precio total:</span>
                      <span className="text-green-600">${precioTotal.toFixed(2)}</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* SECCIÓN EXPANDIBLE: Más opciones */}
        <div className="pt-2 border-t border-gray-200">
          <button
            type="button"
            onClick={() => setMostrarOpciones(!mostrarOpciones)}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-color-primario transition-colors"
          >
            {mostrarOpciones ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
            <span>Más opciones</span>
          </button>

          {mostrarOpciones && (
            <div className="mt-4 space-y-4 animate-fadeIn">
              {/* Referencia */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Referencia <span className="text-gray-400">(opcional)</span>
                </label>
                <input
                  type="text"
                  value={datosProducto.referencia}
                  onChange={(e) => setDatosProducto({ ...datosProducto, referencia: e.target.value })}
                  className="w-full p-2 rounded-xl border-2 outline-none transition-all border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  placeholder="Código interno o SKU"
                />
              </div>

              {/* Unidad de medida */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Unidad de medida
                </label>
                <select
                  value={datosProducto.id_unidad_medida}
                  onChange={(e) => setDatosProducto({ ...datosProducto, id_unidad_medida: parseInt(e.target.value) })}
                  className="w-full p-2 rounded-xl border-2 outline-none transition-all border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                >
                  {unidadesMedida.length === 0 ? (
                    <option value={43}>Unidad (UND)</option>
                  ) : (
                    unidadesMedida.map((u) => (
                      <option key={u.id_unidad_medida} value={u.id_unidad_medida}>
                        {u.descripcion} ({u.simbolo})
                      </option>
                    ))
                  )}
                </select>
              </div>

              {/* Tipo de producto */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de producto
                </label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setDatosProducto({ ...datosProducto, id_tipo_producto: 1 })}
                    className={`flex-1 p-2 rounded-xl border-2 text-sm font-medium transition-all ${
                      datosProducto.id_tipo_producto === 1
                        ? 'border-color-secundario bg-blue-50 text-color-secundario'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    Producto
                  </button>
                  <button
                    type="button"
                    onClick={() => setDatosProducto({ ...datosProducto, id_tipo_producto: 2 })}
                    className={`flex-1 p-2 rounded-xl border-2 text-sm font-medium transition-all ${
                      datosProducto.id_tipo_producto === 2
                        ? 'border-color-secundario bg-blue-50 text-color-secundario'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    Servicio
                  </button>
                </div>
              </div>

              {/* Venta con existencia negativa */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Venta con existencia negativa
                  </label>
                  <p className="text-xs text-gray-500">Permite vender sin stock disponible</p>
                </div>
                <button
                  type="button"
                  onClick={() => setDatosProducto({ ...datosProducto, venta_negativo: !datosProducto.venta_negativo })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    datosProducto.venta_negativo ? 'bg-color-secundario' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      datosProducto.venta_negativo ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* BOTÓN CREAR */}
        <button
          onClick={crearProducto}
          disabled={guardando}
          className="w-full bg-color-secundario text-white py-3 rounded-xl font-semibold hover:bg-opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {guardando ? 'Creando producto...' : 'Crear Producto'}
        </button>
      </div>
    </PlantillaModal>
  );
}

